// Considered: PerspectiveMultiPickerMolecule — rejected because its rows are perspective-assignment
// rows (a person plus a perspective per row); an Archetype row needs a per-row primary radio, an
// excluded state with a reason and an effect preview. Built by composing PickerShellMolecule.
// Considered: the checkbox atom — it has no disabled state, and an excluded row must stay a visible,
// disabled control carrying its reason rather than vanish. See spec Reuse Audit.
import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import DialogWrapper from '../DialogWrapper/DialogWrapper';
import PickerShellMolecule from '../PickerShell/PickerShellMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import type {
  ArchetypeAssignment,
  ArchetypeSelection,
  ArchetypeRowProblem,
  AvailableArchetype,
} from '../../../api/sphereArchetypesApi';
import './ArchetypeMultiPickerMolecule.scss';

/** The server refuses more than this many Archetypes on one sphere. */
export const MAX_ARCHETYPES = 12;

export interface ArchetypeMultiPickerMoleculeProps {
  visible: boolean;
  /** The set currently saved on the sphere; the picker starts from it and reports edits against it. */
  assigned: ArchetypeAssignment[];
  /** What the caller may add, from `GET …/archetypes/available`. */
  available: AvailableArchetype[];
  status: 'loading' | 'ready' | 'error';
  /** Per-row reasons from a 422; the picker stays open and shows each on its row. */
  problems?: ArchetypeRowProblem[];
  /** A 409 was met and the fresh set is already in `assigned`. */
  conflict?: boolean;
  /** The save failed for a reason with no per-row cause (network, 5xx). */
  saveError?: boolean;
  /** The server refused the write (403). */
  forbidden?: boolean;
  saving?: boolean;
  onSave: (selection: ArchetypeSelection) => void;
  onCancel: () => void;
  onRetry?: () => void;
}

interface PickerRow {
  systemName: string;
  displayName: string;
  glyph?: string | null;
  suggestsApproaches: string[];
  excludedBy?: string;
  /** Assigned but no longer resolvable: shown so it can be removed, never made primary. */
  unavailable: boolean;
}

function buildRows(assigned: ArchetypeAssignment[], available: AvailableArchetype[]): PickerRow[] {
  const rows = new Map<string, PickerRow>();
  available.forEach((item) =>
    rows.set(item.systemName, {
      systemName: item.systemName,
      displayName: item.displayName,
      glyph: item.glyph,
      suggestsApproaches: item.suggestsApproaches ?? [],
      excludedBy: item.excludedBy,
      unavailable: false,
    })
  );
  assigned.forEach((item) => {
    if (!rows.has(item.systemName)) {
      rows.set(item.systemName, {
        systemName: item.systemName,
        displayName: item.displayName,
        suggestsApproaches: [],
        unavailable: !item.resolved,
      });
    }
  });
  return Array.from(rows.values());
}

function sameSelection(a: ArchetypeSelection, b: ArchetypeSelection): boolean {
  return a.primary === b.primary && a.archetypes.length === b.archetypes.length && a.archetypes.every((n) => b.archetypes.includes(n));
}

const ArchetypeMultiPickerMolecule: React.FC<ArchetypeMultiPickerMoleculeProps> = ({
  visible,
  assigned,
  available,
  status,
  problems = [],
  conflict = false,
  saveError = false,
  forbidden = false,
  saving = false,
  onSave,
  onCancel,
  onRetry,
}) => {
  const { t } = useTranslation();
  const radioGroupName = useId();

  const saved = useMemo<ArchetypeSelection>(
    () => ({
      archetypes: assigned.map((a) => a.systemName),
      primary: assigned.find((a) => a.isPrimary)?.systemName ?? null,
    }),
    [assigned]
  );
  const [draft, setDraft] = useState<ArchetypeSelection>(saved);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [query, setQuery] = useState('');

  // Reopening, or a 409 that loaded a fresher set, restarts the draft from what is saved.
  const savedKey = `${saved.primary ?? ''}|${saved.archetypes.join(',')}`;
  useEffect(() => {
    if (visible) {
      setDraft(saved);
      setConfirmingDiscard(false);
      setQuery('');
    }
    // `saved` is derived from `savedKey`; restarting on identity would reset the draft on every parent render.
  }, [visible, savedKey]);

  const rows = useMemo(() => buildRows(assigned, available), [assigned, available]);
  const nameOf = (systemName: string) => rows.find((r) => r.systemName === systemName)?.displayName ?? systemName;
  const problemFor = (systemName: string) => problems.find((p) => p.systemName === systemName);
  const dirty = !sameSelection(draft, saved);
  const atLimit = draft.archetypes.length >= MAX_ARCHETYPES;

  const toggle = (row: PickerRow) => {
    setDraft((prev) => {
      if (prev.archetypes.includes(row.systemName)) {
        const archetypes = prev.archetypes.filter((n) => n !== row.systemName);
        const primary =
          prev.primary === row.systemName
            ? archetypes.find((n) => !rows.find((r) => r.systemName === n)?.unavailable) ?? null
            : prev.primary;
        return { archetypes, primary };
      }
      const archetypes = [...prev.archetypes, row.systemName];
      return { archetypes, primary: prev.primary ?? (row.unavailable ? null : row.systemName) };
    });
  };

  const makePrimary = (row: PickerRow) => {
    if (row.unavailable) return;
    setDraft((prev) => (prev.archetypes.includes(row.systemName) ? { ...prev, primary: row.systemName } : prev));
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty && !confirmingDiscard) {
      setConfirmingDiscard(true);
      return;
    }
    onCancel();
  };

  const renderRow = (row: PickerRow) => {
    const selected = draft.archetypes.includes(row.systemName);
    const excluded = Boolean(row.excludedBy) && !selected;
    const limited = atLimit && !selected;
    const disabled = excluded || limited;
    const problem = problemFor(row.systemName);
    const inputId = `${radioGroupName}-${row.systemName}`;
    const reasonId = `${inputId}-reason`;
    let reason: string | null = null;
    if (problem) reason = t(`archetypes.picker.problem.${problem.reason}`);
    else if (excluded) reason = t('archetypes.picker.excludedBy', { name: nameOf(row.excludedBy as string) });
    else if (limited) reason = t('archetypes.picker.limitReached', { max: MAX_ARCHETYPES });
    else if (row.unavailable) reason = t('archetypes.picker.unavailableRow');

    return (
      <div
        className="archetype-multi-picker__row"
        data-selected={selected || undefined}
        data-disabled={disabled || undefined}
        data-problem={problem ? problem.reason : undefined}
        onKeyDown={(e) => {
          // Enter on a selected row makes it primary; Space toggles via the checkbox itself.
          if (e.key === 'Enter' && selected && e.target instanceof HTMLInputElement && e.target.type === 'checkbox') {
            e.preventDefault();
            makePrimary(row);
          }
        }}
      >
        <input
          id={inputId}
          type="checkbox"
          className="archetype-multi-picker__check"
          checked={selected}
          disabled={disabled}
          aria-describedby={reason ? reasonId : undefined}
          onChange={() => toggle(row)}
        />
        <label className="archetype-multi-picker__body" htmlFor={inputId}>
          <span className="archetype-multi-picker__name-line">
            {row.glyph && <GlyphAtom glyph={row.glyph} className="archetype-multi-picker__glyph" aria-hidden />}
            <span className={`archetype-multi-picker__name${row.unavailable ? ' archetype-multi-picker__name--unavailable' : ''}`}>
              {row.displayName}
            </span>
          </span>
          {row.suggestsApproaches.length > 0 && (
            <span className="archetype-multi-picker__suggests">
              {t('archetypes.picker.suggests', { names: row.suggestsApproaches.join(', ') })}
            </span>
          )}
        </label>
        {selected && !row.unavailable && (
          <label className="archetype-multi-picker__primary">
            <input
              type="radio"
              name={radioGroupName}
              checked={draft.primary === row.systemName}
              aria-label={t('archetypes.picker.primaryRadio', { name: row.displayName })}
              onChange={() => makePrimary(row)}
            />
            <span>{t('archetypes.picker.primary')}</span>
          </label>
        )}
        {reason && (
          <span
            id={reasonId}
            className="archetype-multi-picker__reason"
            role={problem ? 'alert' : undefined}
          >
            {reason}
          </span>
        )}
      </div>
    );
  };

  let errorMessage: string | null = null;
  if (status === 'error') errorMessage = t('archetypes.picker.loadErrorBody');

  return (
    <DialogWrapper
      header={t('archetypes.picker.title')}
      visible={visible}
      onHide={requestClose}
      className="archetype-multi-picker-dialog"
    >
      <div className="archetype-multi-picker">
        {conflict && (
          <p className="archetype-multi-picker__banner" role="alert">
            {t('archetypes.picker.conflict')}
          </p>
        )}
        {saveError && (
          <p className="archetype-multi-picker__banner" role="alert">
            {t('archetypes.picker.saveError')}
          </p>
        )}
        {forbidden && (
          <p className="archetype-multi-picker__banner" role="alert">
            {t('archetypes.picker.forbidden')}
          </p>
        )}
        {status !== 'error' && status !== 'loading' && rows.length === 0 ? (
          <EmptyStateAtom icon="pi-tag" title={t('archetypes.picker.empty')} data-testid="archetype-picker-empty" />
        ) : (
          <PickerShellMolecule<PickerRow>
            items={rows}
            getSearchText={(row) => `${row.displayName} ${row.suggestsApproaches.join(' ')}`}
            getRowKey={(row) => row.systemName}
            renderRow={renderRow}
            query={query}
            onQueryChange={setQuery}
            loading={status === 'loading'}
            renderLoading={() => (
              <div data-testid="archetype-picker-loading" aria-busy="true">
                <SkeletonAtom count={4} height="2.5rem" />
              </div>
            )}
            errorMessage={errorMessage}
            errorTitle={status === 'error' ? t('archetypes.picker.loadErrorTitle') : undefined}
            onRetry={onRetry}
            retryLabel={t('archetypes.picker.retry')}
            noMatchesMessage={t('archetypes.picker.noResults')}
            searchPlaceholder={t('archetypes.picker.searchLabel')}
            listLabel={t('archetypes.picker.title')}
            testIdPrefix="archetype-picker"
          />
        )}
        {confirmingDiscard && (
          <div className="archetype-multi-picker__discard" role="alertdialog" aria-labelledby={`${radioGroupName}-discard`}>
            <strong id={`${radioGroupName}-discard`}>{t('archetypes.picker.discardTitle')}</strong>
            <span>{t('archetypes.picker.discardBody')}</span>
            <div className="archetype-multi-picker__actions">
              <ButtonAtom variant="secondary" label={t('archetypes.picker.keepEditing')} onClick={() => setConfirmingDiscard(false)} />
              <ButtonAtom variant="secondary" label={t('archetypes.picker.discard')} onClick={onCancel} />
            </div>
          </div>
        )}
        <div className="archetype-multi-picker__actions">
          <ButtonAtom variant="secondary" label={t('archetypes.picker.cancel')} onClick={requestClose} disabled={saving} />
          <ButtonAtom
            label={saving ? t('archetypes.picker.saving') : t('archetypes.picker.save')}
            disabled={!dirty || saving}
            onClick={() => onSave(draft)}
          />
        </div>
      </div>
    </DialogWrapper>
  );
};

export default ArchetypeMultiPickerMolecule;
